/**
 * Codec policy: make the host SEND with a codec it can encode in hardware.
 *
 * Two mechanisms, one goal:
 *
 * 1. GUEST offer shaping (`restrictToHardwareVideoCodecs`) — the guest is the offerer, and
 *    its receive-preference order is what the answering host's send codec follows by
 *    default (RFC 3264: you send what the remote listed first among what you support).
 *    Android and desktop Chromium offer HEVC first. Apple keeps HEVC available for explicit
 *    trials but offers fleet-hardware H.264 first. Firefox stays on its own order. AV1 stays
 *    dropped for every guest.
 *
 * 2. HOST send-codec pinning (`chooseHardwareSendCodec`) — offer shaping alone cannot pick
 *    the right H.264 PROFILE, because profiles differ per host encoder (measured on an AMD
 *    encoder: 42e01f has NO hardware encoder at any resolution while 42001f/4d001f/640020
 *    are hardware at every resolution) and the guest cannot know the host's encoder. The
 *    answerer's own setCodecPreferences does NOT change what it sends — verified in a live
 *    Electron loopback: with the answer reordered 42001f-first, the sender still encoded
 *    42e01f, honoring the OFFER's order. The lever that actually works (verified in the
 *    same loopback) is RTCRtpSender.setParameters with encodings[0].codec, which pins the
 *    send codec to any member of the NEGOTIATED set after negotiation, with no
 *    renegotiation and no session disruption.
 *
 * PRODUCT RULE (2026-09-25): the host NEVER pins a software encoder. Every host this ships
 * to has hardware H.264, so "nothing probed hardware" means the probe misread the machine,
 * not that the machine lacks one. When a candidate's own profile probes software, it gets a
 * second probe as its parent (unconstrained) profile — the same silicon encodes both, and
 * per-profile encodingInfo verdicts are untrusted (field: AMD). A constrained-profile entry
 * whose parent probes hardware is pinnable; a profile whose parent also probes software is
 * left as the negotiated default rather than actively pinned to OpenH264.
 */

import { videoCodecFamily } from './preferHardwareVideoCodecs';

/**
 * PRODUCT DECISION — currently ON.
 *
 * When true, the guest's offer is restricted to H.264 + H.265 (plus rtx/red/ulpfec and any
 * unrecognised auxiliary codecs). Turning this off restores full-family negotiation.
 */
export const RESTRICT_VIDEO_TO_H264 = true;

/**
 * Families excluded from the guest offer, by guest engine.
 *
 * HEVC is the first choice for Apple, Android, and desktop Chromium. The
 * "guaranteed hardware" requirement is met by the pin pipeline, not by the offer: the offer
 * only decides what CAN be negotiated, while `chooseHardwareSendCodec` decides what IS used.
 * It pins a codec only when the HOST's own probe reports hardware encode AND the guest's
 * published `hardwareDecodeFamilies` says hardware decode. An Android device without hardware
 * HEVC decode publishes no h265 and the pin stays on H.264; a host whose AMD encode probe
 * misreports is caught by the sustained-software escalation (SoftwareEncodeRecoveryController).
 * AV1 stays dropped everywhere: most Android phones decode it in software, and a
 * software-DECODED stream is worse than hardware H.264 — the original AV1 field lesson.
 *
 * Apple, Android, and desktop Chromium keep HEVC. Android and desktop Chromium list it first;
 * Apple lists fleet-hardware H.264 first after high-motion HEVC cadence failures in field
 * measurements. Firefox offers drop it. AV1 stays dropped for every guest.
 *
 * Excluding a family from being SENT IN SOFTWARE is a separate concern, handled where the
 * evidence exists — SOFTWARE_FALLBACK_ORDER on the host, after probing.
 */
const DROPPABLE_VIDEO_FAMILIES = new Set<string>(['h265', 'av1']);
const DROPPABLE_VIDEO_FAMILIES_APPLE = new Set<string>(['av1']);

export type CodecPolicyResult<T> = {
  /** The codec list to hand to setCodecPreferences. */
  codecs: T[];
  /** True when the list was narrowed under the policy. */
  restricted: boolean;
};

type CodecLike = { mimeType: string };

/**
 * Narrow a GUEST receive-preference list to hardware-friendly families.
 *
 * Fails open: a list with no H.264 at all is returned untouched. Restricting it would
 * produce an empty codec intersection and the call would fail to negotiate entirely, which
 * is worse than a software encode. Otherwise, removes AV1 and, unless `keepHevcFirst`,
 * H.265. `keepHevc` retains H.265 for capable guests; `preferHevcFirst` optionally moves it
 * ahead of the other surviving codecs. Relative order within each group is preserved.
 */
export function restrictToHardwareVideoCodecs<T extends CodecLike>(
  codecs: T[],
  keepHevc = false,
  preferHevcFirst = keepHevc,
): CodecPolicyResult<T> {
  if (!RESTRICT_VIDEO_TO_H264) return { codecs, restricted: false };

  const hasH264 = codecs.some((codec) => videoCodecFamily(codec.mimeType) === 'h264');
  if (!hasH264) return { codecs, restricted: false };

  const drop = keepHevc ? DROPPABLE_VIDEO_FAMILIES_APPLE : DROPPABLE_VIDEO_FAMILIES;
  const kept = codecs.filter((codec) => !drop.has(videoCodecFamily(codec.mimeType)));
  if (!preferHevcFirst) return { codecs: kept, restricted: true };

  const hevc = kept.filter((codec) => videoCodecFamily(codec.mimeType) === 'h265');
  const rest = kept.filter((codec) => videoCodecFamily(codec.mimeType) !== 'h265');
  return { codecs: [...hevc, ...rest], restricted: true };
}

/**
 * H.264 profile-level-id prefixes the host fleet can encode in hardware.
 *
 * Measured on an AMD host encoder (mediaCapabilities.encodingInfo, prefer-hardware):
 * 4200xx (Baseline), 4d00xx (Main) and 6400xx (High) all report hardware at every tested
 * resolution, while the CONSTRAINED variants 42e0xx (Constrained Baseline) and 640cxx
 * (Constrained High) report software at every resolution. Safari-class guests offer ONLY
 * the constrained variants, which is why they land on OpenH264 despite the host having a
 * perfectly good hardware encoder.
 */
// Constrained profiles (42e0, 640c) are hardware on this encoder too. The old list
// left them out from an AMD encodingInfo reading and that is what forced HEVC.
// const FLEET_HARDWARE_H264_PROFILE_PREFIXES = ['4200', '4d00', '6400'];
const FLEET_HARDWARE_H264_PROFILE_PREFIXES = ['4200', '4d00', '6400', '42e0', '640c'];

/** True when a guest-offered H.264 profile has a hardware encoder on the host fleet. */
export function h264ProfileHasFleetHardwareEncoder(profile: string): boolean {
  return FLEET_HARDWARE_H264_PROFILE_PREFIXES.some((prefix) => profile.startsWith(prefix));
}

export type OfferOrderResult<T> = {
  codecs: T[];
  /** True when H.265 was moved to the front of the offer. */
  hevcPromoted: boolean;
};

/**
 * Promote H.265 to the front of a guest offer whose H.264 profiles are all software-only
 * on the host fleet.
 *
 * The host's send codec defaults to the first codec in the OFFER it can send (RFC 3264).
 * A Safari-shape offer — H.264 constrained profiles only, then H.265 — therefore lands the
 * host on a software H.264 encoder even though both ends have hardware H.265. Putting
 * H.265 first makes the hardware-hardware path the DEFAULT rather than something the
 * host-side pin has to rescue after the fact.
 *
 * Deliberately conservative: it only fires when H.265 is present AND at least one H.264
 * profile is offered AND none of the offered H.264 profiles has a fleet hardware encoder.
 * Guests that offer 42001f/4d001f/6400xx (Chrome-class) are left exactly as-is, and the
 * host-side pin remains the corrector on hosts where the H.265 default turns out to lack
 * a hardware encoder.
 */
export function promoteHevcWhenH264IsSoftwareOnly<T extends NegotiatedCodecLike>(codecs: T[]): OfferOrderResult<T> {
  const h264Profiles = codecs
    .filter((c) => videoCodecFamily(c.mimeType) === 'h264')
    .map((c) => h264ProfileOf(c.sdpFmtpLine))
    .filter((p): p is string => !!p);
  const hevc = codecs.filter((c) => videoCodecFamily(c.mimeType) === 'h265');

  if (hevc.length === 0 || h264Profiles.length === 0) {
    return { codecs, hevcPromoted: false };
  }
  if (h264Profiles.some(h264ProfileHasFleetHardwareEncoder)) {
    return { codecs, hevcPromoted: false };
  }

  const rest = codecs.filter((c) => videoCodecFamily(c.mimeType) !== 'h265');
  return { codecs: [...hevc, ...rest], hevcPromoted: true };
}

/** Extracts the profile-level-id that identifies an H.264 profile, lower-cased. */
export function h264ProfileOf(sdpFmtpLine?: string): string | undefined {
  return /profile-level-id=([0-9a-fA-F]+)/.exec(sdpFmtpLine ?? '')?.[1]?.toLowerCase();
}

/** A codec entry as it appears in RTCRtpSendParameters.codecs (the negotiated set). */
export type NegotiatedCodecLike = {
  mimeType: string;
  clockRate?: number;
  sdpFmtpLine?: string;
};

/**
 * Probe signature narrowed from navigator.mediaCapabilities.encodingInfo. Injected so the
 * selection is testable without a browser. The contentType carries the exact codec string
 * under test.
 */
export type EncodeProfileProbe = (contentType: string) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;

export type SendCodecDecision<T> = {
  /**
   * The codec to pin, or null when there is nothing to do — no hardware send codec could
   * be established, or the hardware choice is already the negotiated default.
   */
  codec: T | null;
  /** The browser does not expose an encoding capability probe. */
  probeUnavailable?: boolean;
  /** Set by the sender transaction; false means the browser rejected the pin. */
  pinAccepted?: boolean;
  /** Why the sender tried negotiated H.264 instead of the preferred HEVC codec. */
  fallbackReason?: string;
  /** Codec strings considered, in negotiated order. */
  considered: string[];
  /**
   * Codec strings that are hardware at BOTH ends — the host's encode probe said so and,
   * where the guest reported its decode capability, the guest can decode them in hardware
   * too. Every downstream reader treats a non-empty list as "a real hardware path was
   * found", which is only true end to end.
   */
  hardware: string[];
  /**
   * Candidates the HOST encodes in hardware but the GUEST cannot decode in hardware.
   * Empty when the guest's capability is unknown. Surfaced in the overlay so a field
   * screenshot distinguishes "this host has no hardware" from "no hardware both ends".
   */
  guestSoftwareDecode?: string[];
};

/**
 * Families the host may pin to when a HARDWARE encoder exists, best first.
 *
 * H.265 leads. At equal picture quality it costs roughly half the bitrate of H.264, which is
 * the difference that matters most on exactly the constrained mobile links this path exists
 * to serve. There is no compatibility argument against it: H.265 is only ever IN the
 * negotiated set because the guest offered it, which means the guest can decode it.
 *
 * AV1 follows H.265: better compression still, but its hardware encoders are newer and less
 * proven at low latency, so it does not displace the mature choice. VP9 and VP8 trail the
 * H.26x pair. EVERY family appears here, because hardware is always the ideal outcome — a
 * hardware VP9 path beats falling to software H.264, and nothing may be excluded from this
 * list on the grounds that it is usually software.
 */
// Firefox pins hardware H.264 only. Apple, Android, and desktop Chromium pin HEVC first and
// fall back to H.264 when the host probe does not find a hardware HEVC encoder.
// const PIN_FAMILY_ORDER = ['h265', 'av1', 'h264', 'vp9', 'vp8'];
const PIN_FAMILY_ORDER = ['h264'];
const APPLE_PIN_FAMILY_ORDER = ['h265', 'h264'];
const H264_FLOOR_FAMILY_ORDER = PIN_FAMILY_ORDER;

/** One encodingInfo call, bounded by PROBE_TIMEOUT_MS; throws/timeout = unknown. */
async function probeContentType(
  probe: EncodeProfileProbe,
  contentType: string,
): Promise<{ supported?: boolean; powerEfficient?: boolean } | null> {
  try {
    const probePromise = probe(contentType);
    // Attach a no-op catch to suppress late rejections after a timeout wins the race.
    probePromise.catch(() => {});
    return await Promise.race([
      probePromise,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), PROBE_TIMEOUT_MS)),
    ]);
  } catch {
    // Unknown, not software — an unreadable probe must not steer the choice.
    return null;
  }
}

/**
 * How long a single capability probe may take before it is treated as "unknown".
 *
 * encodingInfo is a capability lookup and should answer in microseconds, but on a cold GPU
 * process it has been observed slow, and a promise that never settles would otherwise hold
 * the whole decision open — every other candidate denied its verdict by one straggler, and
 * the stream left on the negotiated default indefinitely. Connection speed is a requirement,
 * so an unanswered probe loses its vote rather than blocking the vote.
 *
 * A timed-out probe is treated exactly like a thrown one: unknown, which is NOT hardware. It
 * can only cost a pin that would have been an upgrade, never cause a wrong one.
 */
const PROBE_TIMEOUT_MS = 500;

function contentTypeFor(codec: NegotiatedCodecLike): string {
  const family = videoCodecFamily(codec.mimeType);
  if (family === 'vp8') return 'video/VP8';
  if (family === 'vp9') return 'video/VP9';
  if (family === 'av1') return 'video/AV1';
  if (family === 'h265') {
    // The negotiated H.265 fmtp carries the RECEIVER's decode ceiling and transport
    // params, not encode requirements — e.g. level-id=180 (level 6.0) from a guest that
    // can DECODE up to 6.0. Probing the ENCODER with that string reports unsupported even
    // when hardware HEVC encode exists (measured: level-id=180 -> supported=false while
    // profile-id alone -> hardware). The sender encodes at or below the ceiling, so only
    // the profile identifies the encoder.
    const profileId = /profile-id=(\d+)/.exec(codec.sdpFmtpLine ?? '')?.[1];
    return 'video/H265;profile-id=' + (profileId ?? '1');
  }
  return codec.sdpFmtpLine ? 'video/H264;' + codec.sdpFmtpLine : 'video/H264';
}

/**
 * Split the host's hardware-encodable candidates by whether the GUEST decodes them in
 * hardware too.
 *
 * `undefined` is UNKNOWN, not "none". A guest whose probe has not resolved yet, or one that
 * predates the field entirely, must produce byte-identical decisions to the host-only
 * behaviour — so every candidate survives and nothing is reported as guest-software. An
 * empty array is the opposite kind of answer: the guest asked its platform and got nothing
 * back, so no candidate is hardware end to end and the choice drops to the software ladder.
 *
 * Guest family names are normalised through videoCodecFamily, so 'HEVC', 'AVC' and
 * 'video/AV1' name the same families this module reasons about rather than reading as
 * unknown strings that would silently discard a hardware-hardware path.
 */
function splitByGuestDecodeCapability<T extends NegotiatedCodecLike>(
  hostHardware: T[],
  guestHardwareDecodeFamilies: string[] | undefined,
): { bothEnds: T[]; guestSoftware: T[] } {
  if (guestHardwareDecodeFamilies === undefined) {
    return { bothEnds: hostHardware, guestSoftware: [] };
  }

  const guestFamilies = new Set(guestHardwareDecodeFamilies.map(videoCodecFamily));
  const bothEnds: T[] = [];
  const guestSoftware: T[] = [];
  for (const codec of hostHardware) {
    if (guestFamilies.has(videoCodecFamily(codec.mimeType))) bothEnds.push(codec);
    else guestSoftware.push(codec);
  }
  return { bothEnds, guestSoftware };
}

/**
 * Choose a negotiated send codec under the host hardware policy, retaining negotiated
 * order within each preferred family. Always returns the selected codec even when it
 * is already the negotiated default; a null codec means no candidate qualified.
 *
 * A hardware probe hit for any H.264 profile qualifies every negotiated H.264 profile.
 * A constrained-only H.264 set can also qualify through the fleet profile allowlist.
 * Probe errors and 500 ms timeouts are treated as unknown, so that fallback can still
 * select H.264. An absent probe returns a null codec without applying the fallback.
 *
 * @param guestHardwareDecodeFamilies Guest decode families; undefined skips the guest
 *   filter and an empty list rejects all candidates. If a nonempty list omits H.264
 *   and no shared candidate remains, host-qualified H.264 is admitted as a fallback.
 * @param preferH264BitrateFloor Selects the H.264-only order, currently also the default.
 * @param preferHevcFirst Tries HEVC before H.264 and takes precedence over
 *   preferH264BitrateFloor; other families are excluded.
 */
export async function chooseHardwareSendCodec<T extends NegotiatedCodecLike>(
  negotiated: T[],
  probe: EncodeProfileProbe | null,
  guestHardwareDecodeFamilies?: string[],
  preferH264BitrateFloor = false,
  preferHevcFirst = false,
): Promise<SendCodecDecision<T>> {
  const label = (c: T) => {
    const profile = h264ProfileOf(c.sdpFmtpLine);
    return c.mimeType + (profile ? ':' + profile : '');
  };

  const familyOrder = preferHevcFirst
    ? APPLE_PIN_FAMILY_ORDER
    : preferH264BitrateFloor
      ? H264_FLOOR_FAMILY_ORDER
      : PIN_FAMILY_ORDER;
  const candidates = negotiated.filter((c) => familyOrder.includes(videoCodecFamily(c.mimeType)));
  const considered = candidates.map(label);
  if (!probe || candidates.length === 0) {
    return { codec: null, considered, hardware: [], guestSoftwareDecode: [], probeUnavailable: !probe };
  }

  const hostEncodesInHardware = new Set<T>();
  await Promise.all(
    candidates.map(async (codec) => {
      const info = await probeContentType(probe, contentTypeFor(codec));
      if (info?.supported && info.powerEfficient) hostEncodesInHardware.add(codec);
    }),
  );
  // Profiles this probe actually marked hardware. Later expansions can add a
  // fallback, but selection prefers a profile with its own probe hit.
  const directlyProbed = new Set(hostEncodesInHardware);

  // One hardware H.264 encoder covers every profile. A per-profile encodingInfo miss
  // (constrained baseline reporting software while baseline reports hardware) does not
  // take the other profiles off the pin.
  const h264EncoderIsHardware = candidates.some(
    (codec) => videoCodecFamily(codec.mimeType) === 'h264' && hostEncodesInHardware.has(codec),
  );
  if (h264EncoderIsHardware) {
    for (const codec of candidates) {
      if (videoCodecFamily(codec.mimeType) === 'h264') hostEncodesInHardware.add(codec);
    }
  }

  // Safari negotiates only constrained profiles. encodingInfo reports those software
  // even when the GPU encodes them, and there is no baseline sibling for the expansion
  // above to catch. The fleet list is the H.264 fallback when HEVC did not probe hardware.
  const h264Candidates = candidates.filter((codec) => videoCodecFamily(codec.mimeType) === 'h264');
  const constrainedOnly =
    h264Candidates.length > 0 &&
    h264Candidates.every((codec) => {
      const profile = h264ProfileOf(codec.sdpFmtpLine) ?? '';
      return profile.startsWith('42e0') || profile.startsWith('640c');
    });
  const h264ProbedHardware = h264Candidates.some((codec) => hostEncodesInHardware.has(codec));
  if (constrainedOnly && !h264ProbedHardware) {
    for (const codec of h264Candidates) {
      const profile = h264ProfileOf(codec.sdpFmtpLine);
      if (profile && h264ProfileHasFleetHardwareEncoder(profile)) hostEncodesInHardware.add(codec);
    }
  }

  // Read back in negotiated order rather than in probe-resolution order, so the reported
  // sets are deterministic regardless of which probe answers first.
  let { bothEnds: hardware, guestSoftware } = splitByGuestDecodeCapability(
    candidates.filter((c) => hostEncodesInHardware.has(c)),
    guestHardwareDecodeFamilies,
  );
  // An iPhone publishes HEVC as its hardware decode family. When the host did not find a
  // hardware HEVC encoder, that list would veto every H.264 profile and the sender stays
  // off. H.264 is the fallback the phone can still decode. An empty list still means the
  // guest probed and found nothing.
  const guestNamedH264 =
    guestHardwareDecodeFamilies?.some((family) => videoCodecFamily(family) === 'h264') === true;
  if (
    hardware.length === 0 &&
    guestHardwareDecodeFamilies !== undefined &&
    guestHardwareDecodeFamilies.length > 0 &&
    !guestNamedH264
  ) {
    const h264 = guestSoftware.filter((codec) => videoCodecFamily(codec.mimeType) === 'h264');
    if (h264.length > 0) {
      hardware = h264;
      guestSoftware = guestSoftware.filter((codec) => videoCodecFamily(codec.mimeType) !== 'h264');
    }
  }
  const guestSoftwareDecode = guestSoftware.map(label);

  if (hardware.length === 0) {
    // PRODUCT RULE: the host never pins a software encoder. Every shipped host has hardware
    // H.264, so an empty hardware set means the probe misread the machine — the negotiated
    // default stands and the sustained-software path keeps re-probing rather than actively
    // steering the session to OpenH264. (The former SOFTWARE_FALLBACK_ORDER pin here was the
    // bug: it is what put Safari sessions on a software encoder.)
    return { codec: null, considered, hardware: [], guestSoftwareDecode };
  }

  let best: T | null = null;
  for (const family of familyOrder) {
    // directlyProbed wins inside a family, but only among codecs the guest can decode.
    // A host-side HEVC hit must not pin a phone that published no h265.
    best = candidates.find((c) => videoCodecFamily(c.mimeType) === family && directlyProbed.has(c) && hardware.includes(c)) ?? null;
    if (best) break;
  }
  if (!best) {
    for (const family of familyOrder) {
      best = candidates.find((c) => videoCodecFamily(c.mimeType) === family && hardware.includes(c)) ?? null;
      if (best) break;
    }
  }

  return {
    codec: best,
    considered,
    hardware: hardware.map(label),
    guestSoftwareDecode,
  };
}
